import React, { useState } from 'react';
import { adminAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { KeyRound, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Modal that blocks the app until an invited admin changes their temporary password.
 * Triggered when admin.must_change_password === true.
 * Cannot be dismissed (no overlay-close, no cancel button).
 */
export default function ForcePasswordChangeModal() {
  const { admin, updateAdmin } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const open = !!admin?.must_change_password;
  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await adminAPI.update(admin.id, { password });
      updateAdmin({ must_change_password: false });
      toast.success('Password updated — welcome aboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={() => { /* intentionally non-dismissable */ }}>
      <DialogContent
        className="max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="force-password-change-modal"
      >
        <DialogHeader>
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
            <ShieldCheck className="w-6 h-6 text-primary" />
          </div>
          <DialogTitle>Set a new password to continue</DialogTitle>
          <DialogDescription>
            Welcome, {admin?.name?.split(' ')[0] || admin?.email}. For your security, please replace the temporary password your owner set with one you choose. You won't be able to use the app until you do.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="fpc-pw">New password (min 8 characters)</Label>
            <Input
              id="fpc-pw"
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="fpc-new-password"
              required
              minLength={8}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="fpc-confirm">Confirm new password</Label>
            <Input
              id="fpc-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="fpc-confirm-password"
              required
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="w-full gap-2"
            data-testid="fpc-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            Set new password
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
